import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { useAuth } from "./useAuth"
import { toast } from "sonner"
import { addMonths, format } from "date-fns"

export interface FinancialCharge {
  id: string
  condominium_id: string
  unit_id: string
  resident_id?: string
  description: string
  amount_cents: number
  currency: string
  due_date: string
  status: 'pending' | 'paid' | 'canceled' | 'overdue'
  payment_method?: string
  external_payment_link?: string
  external_reference?: string
  created_by: string
  paid_at?: string
  notes?: string
  created_at: string
  updated_at: string
  // Joined data
  unit?: {
    number: string
    building?: string
  }
  resident?: {
    full_name: string
    email: string
  }
}

export const useFinancialCharges = () => {
  const { profile } = useAuth()
  
  return useQuery({
    queryKey: ["financial-charges", profile?.condominium_id],
    queryFn: async () => {
      if (!profile?.condominium_id) return []

      const { data, error } = await supabase
        .from("financial_charges")
        .select(`
          *,
          unit:units!unit_id(number, building),
          resident:profiles!resident_id(full_name, email)
        `)
        .eq("condominium_id", profile.condominium_id)
        .order("created_at", { ascending: false })

      if (error) throw error
      return data as FinancialCharge[]
    },
    enabled: !!profile?.condominium_id
  })
}

export const useCreateFinancialCharge = () => {
  const queryClient = useQueryClient()
  const { profile } = useAuth()

  return useMutation({
    mutationFn: async (chargeData: Omit<FinancialCharge, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'condominium_id'>) => {
      if (!profile?.id || !profile?.condominium_id) {
        throw new Error("Usuário não autenticado")
      }

      const { data, error } = await supabase
        .from("financial_charges")
        .insert({
          ...chargeData,
          created_by: profile.id,
          condominium_id: profile.condominium_id
        })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["financial-charges"] })
      toast.success("Cobrança criada com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao criar cobrança:", error)
      toast.error("Erro ao criar cobrança")
    }
  })
}

export const useUpdateFinancialCharge = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<FinancialCharge> }) => {
      const { data, error } = await supabase
        .from("financial_charges")
        .update(updates)
        .eq("id", id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["financial-charges"] })
      toast.success("Cobrança atualizada com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao atualizar cobrança:", error)
      toast.error("Erro ao atualizar cobrança")
    }
  })
}

export const useDeleteFinancialCharge = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("financial_charges")
        .delete()
        .eq("id", id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["financial-charges"] })
      toast.success("Cobrança removida com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao remover cobrança:", error)
      toast.error("Erro ao remover cobrança")
    }
  })
}

export const useFinancialStats = () => {
  const { profile } = useAuth()
  
  return useQuery({
    queryKey: ["financial-stats", profile?.condominium_id],
    queryFn: async () => {
      if (!profile?.condominium_id) return null

      const { data, error } = await supabase
        .from("financial_charges")
        .select("status, amount_cents")
        .eq("condominium_id", profile.condominium_id)

      if (error) throw error

      const stats = data.reduce((acc, charge) => {
        acc.total_charges += 1
        acc.total_amount += charge.amount_cents
        
        switch (charge.status) {
          case 'pending':
            acc.pending_charges += 1
            acc.pending_amount += charge.amount_cents
            break
          case 'paid':
            acc.paid_charges += 1
            acc.paid_amount += charge.amount_cents
            break
          case 'overdue':
            acc.overdue_charges += 1
            acc.overdue_amount += charge.amount_cents
            break
        }
        
        return acc
      }, {
        total_charges: 0,
        total_amount: 0,
        pending_charges: 0,
        pending_amount: 0,
        paid_charges: 0,
        paid_amount: 0,
        overdue_charges: 0,
        overdue_amount: 0
      })

      return stats
    },
    enabled: !!profile?.condominium_id
  })
}

interface CreateChargesBatchParams {
  unit_id: string
  resident_id?: string
  description: string
  amount_cents: number
  start_date: string
  end_date: string
  payment_method?: string
  external_payment_link?: string
  notes?: string
}

export const useCreateFinancialChargesBatch = () => {
  const queryClient = useQueryClient()
  const { profile } = useAuth()

  return useMutation({
    mutationFn: async (params: CreateChargesBatchParams) => {
      if (!profile?.id || !profile?.condominium_id) {
        throw new Error("Usuário não autenticado")
      }

      // Generate charges from start_date to end_date (monthly)
      const charges = []
      let currentDate = new Date(params.start_date)
      const endDate = new Date(params.end_date)

      while (currentDate <= endDate) {
        charges.push({
          condominium_id: profile.condominium_id,
          created_by: profile.id,
          unit_id: params.unit_id,
          resident_id: params.resident_id,
          description: params.description,
          amount_cents: params.amount_cents,
          due_date: format(currentDate, 'yyyy-MM-dd'),
          payment_method: params.payment_method,
          external_payment_link: params.external_payment_link,
          notes: params.notes,
          status: 'pending',
          currency: 'BRL'
        })
        
        currentDate = addMonths(currentDate, 1)
      }

      const { data, error } = await supabase
        .from("financial_charges")
        .insert(charges)
        .select()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["financial-charges"] })
      toast.success("Cobranças automáticas criadas com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao criar cobranças:", error)
      toast.error("Erro ao criar cobranças automáticas")
    }
  })
}